import { Box, Button, DialogActions, DialogContent, DialogTitle, Divider, FormHelperText, Modal, Paper, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Decimal from "decimal.js";
import CurrencyTextField from "../../../../../components/shared/fields/CurrencyTextField";
import TooltipFineAndInterest from "./TooltiFineAndInterest";
import TooltipReceivedValue from "./TooltipReceivedValue";
import dayjs from "../../../../helpers/dayjsConfig";
import ErrorBox from "../../../../../components/shared/boxes/ErrorBox";
import { useEffect, useState } from "react";


const InstallmentPaymentModal = ({ open, onClose, selectedRow, paymentDate, setPaymentDate, fineAndInterestValue, setFineAndInterestValue, onConfirm, loading, postInstallmentsApiError, setPostInstallmentsApiError }) => {
    const [dateError, setDateError] = useState(false);
    const [fineAndInterestError, setFineAndInterestError] = useState(false);

    const isConfirmDisabled = Boolean(
        !paymentDate ||
        loading ||
        dateError ||
        fineAndInterestError ||
        postInstallmentsApiError
    );

    const modalOnClose = () => {
        setDateError(false);
        onClose();
    }

    useEffect(() => {
        return () => {
            setDateError(false);
        }
    }, [])

    return (
        <>
            <Modal open={open} onClose={modalOnClose} sx={{ height: '100vh', width: '100vw', display: 'flex', overflow: 'auto' }}>
                <Paper
                    elevation={16}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        p: '1rem',
                        borderRadius: '0.25rem',
                        maxWidth: '28rem',
                        mx: "auto",
                        my: "auto"
                    }}
                >

                    <DialogTitle
                        sx={{
                            p: '1rem 1.5rem',
                        }}>
                        <Typography variant="h5">
                            Definir como paga
                        </Typography>
                    </DialogTitle>
                    {selectedRow && (
                        <>
                            <DialogContent sx={{ py: '1.5rem', pb: '1.25rem' }}>
                                {postInstallmentsApiError && (
                                    <ErrorBox
                                        errorMessage={postInstallmentsApiError}
                                        onClose={() => setPostInstallmentsApiError(null)}
                                        sx={{ mb: '1.25rem' }}
                                    />
                                )}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                                    <Box>
                                        <Typography variant="h6"> Parcela </Typography>
                                        <Typography>{selectedRow.installmentNumber}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="h6"> Valor cobrado</Typography>
                                        <Typography>{selectedRow.value}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="h6"> Vencimento </Typography>
                                        <Typography>{selectedRow.due_date.toLocaleDateString("pt-BR")}</Typography>
                                    </Box>
                                </Box>
                                <Divider sx={{ my: '1.25rem' }} />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.875rem' }}>
                                    <Box>
                                        <Box>
                                            <Typography variant="h6">Pagamento</Typography>
                                        </Box>
                                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                                            <DatePicker
                                                value={paymentDate}
                                                onChange={(newValue) => setPaymentDate(newValue)}
                                                sx={{ width: '100%' }}
                                                disabled={loading}
                                                maxDate={dayjs()}
                                                onError={(reason, value) => {
                                                    setDateError(reason);
                                                }}
                                            />
                                        </LocalizationProvider>
                                        {dateError && (
                                            <FormHelperText variant="helperError" sx={{ px: '0.875rem' }}>
                                                Deve ser anterior ou igual a data de hoje
                                            </FormHelperText>
                                        )}
                                    </Box>
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="h6">Juros e multas</Typography>
                                            <TooltipFineAndInterest />
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <CurrencyTextField
                                                currencySymbol="R$"
                                                variant="outlined"
                                                outputFormat="number"
                                                decimalCharacter=","
                                                digitGroupSeparator="."
                                                placeholder="0,00"
                                                sx={{ maxWidth: '100%' }}
                                                value={fineAndInterestValue}
                                                error={fineAndInterestError}
                                                disabled={loading}
                                                onChange={(e, value) => {
                                                    setFineAndInterestValue(value);

                                                    const installmentValue = new Decimal(
                                                        selectedRow.value.replace(/[R$\s.]/g, "").replace(",", ".")
                                                    );

                                                    if (new Decimal(value || 0).greaterThan(installmentValue)) {
                                                        setFineAndInterestError(true);
                                                    } else {
                                                        setFineAndInterestError(false);
                                                    }
                                                }}
                                            />
                                        </Box>
                                        {fineAndInterestError && (
                                            <FormHelperText variant="helperError" sx={{ px: '0.875rem' }}>
                                                Deve ser menor ou igual ao valor cobrado
                                            </FormHelperText>
                                        )}
                                    </Box>
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="h6">Valor recebido</Typography>
                                            <TooltipReceivedValue />
                                        </Box>
                                        <TextField
                                            value={
                                                selectedRow
                                                    ? `R$ ${new Decimal(
                                                        selectedRow.value
                                                            .replace(/[R$\s.]/g, "")
                                                            .replace(",", ".") || 0
                                                    )
                                                        .plus(new Decimal(fineAndInterestValue || 0))
                                                        .toFixed(2)
                                                        .replace(".", ",")}`
                                                    : ""
                                            }
                                            disabled
                                            sx={{ width: '100%' }}
                                        />
                                    </Box>
                                </Box>
                            </DialogContent>
                        </>
                    )}
                    <DialogActions sx={{ display: 'flex' }}>
                        <Button
                            sx={{ mt: 2 }}
                            variant="cancelDialog"
                            color="secondary"
                            onClick={modalOnClose}
                        >
                            Cancelar
                        </Button>
                        <Button
                            sx={{ mt: 2 }}
                            variant="contained"
                            color="secondary"
                            onClick={onConfirm}
                            loading={loading}
                            loadingPosition="start"
                            disabled={isConfirmDisabled}
                        >
                            Confirmar
                        </Button>
                    </DialogActions>
                </Paper>
            </Modal>
        </>
    )
}

export default InstallmentPaymentModal;